/**
 * Modeville product gallery: <mv-gallery> core (photos, thumbnails, colour filter, 3 second autoplay).
 * Markup and data: snippets/product-media-gallery-content.liquid. Swipe and zoom: mv-gallery-ui.js.
 *
 * Autoplay cycles only the suitable garment photos (collages, size charts and swatches are flagged in the data
 * and stay reachable through the thumbnails). It stops, until the visitor presses Play, when the visitor
 * clicks a colour, thumbnail or arrow, swipes, uses the arrow keys, opens zoom or presses Pause. The colour the
 * picker preselects on load is not a click. Size changes never touch the gallery, which also ignores Horizon's
 * variant-update event so it is never rebuilt. Disabled for reduced motion; pauses while the tab is hidden.
 */
(() => {
  'use strict';
  if (window.MvGalleryBase) return;

  const SWIPE_PX = 40;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * @param {string} src
   * @param {number} width
   */
  const sized = (src, width) => (/[?&]width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, `$1width=${width}`) : `${src}${src.includes('?') ? '&' : '?'}width=${width}`);

  /**
   * @param {string} src
   */
  const srcset = (src) => [600, 900, 1200, 1600].map((w) => `${sized(src, w)} ${w}w`).join(', ');

  class MvGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      const dataEl = this.querySelector('script[data-mv-gallery-data]');
      if (!dataEl) return;

      try {
        this.items = JSON.parse(dataEl.textContent || '[]');
      } catch (error) {
        return;
      }
      if (!this.items.length) return;
      this._ready = true;

      this.intervalMs = Number(this.dataset.autoplayMs) || 3000;
      this.colourOption = (this.dataset.colourOption || '').trim().toLowerCase();
      this.productId = this.dataset.productId || '';
      this.zoomEnabled = this.dataset.zoom !== 'false';

      this.stage = this.querySelector('[data-stage]');
      this.frame = this.querySelector('[data-frame]');
      this.layers = Array.from(this.querySelectorAll('[data-layer]'));
      this.thumbsBox = this.querySelector('[data-thumbs]');
      this.thumbItems = new Map(Array.from(this.querySelectorAll('[data-thumb-item]')).map((el) => [Number(el.dataset.id), el]));
      this.playButton = this.querySelector('[data-play]');
      this.status = this.querySelector('[data-status]');
      this.dialog = this.querySelector('[data-dialog]');
      this.zoomScroller = this.querySelector('[data-zoom-scroller]');
      this.zoomImg = this.querySelector('[data-zoom-img]');

      this.colour = null;
      this.playing = false;
      this.timer = 0;
      this.token = 0;
      this.activeLayer = 0;
      this.drag = null;
      this.lastDragAt = 0;

      this.byId = new Map(this.items.map((item) => [item.id, item]));
      this.current = this.byId.get(Number(this.dataset.firstId)) || this.items[0];

      this.bind();

      const chosen = (this.dataset.chosenColour || '').trim();
      if (chosen) {
        // The visitor arrived with a colour already chosen (?variant=...): show that colour, no autoplay.
        this.applyColour(chosen);
        const own = this.items.find((item) => item.colours.includes(chosen));
        if (own) this.show(own, { instant: true });
        this.setAttribute('data-mode', 'manual');
        this.setAttribute('data-playing', 'false');
      } else {
        this.applyVisibility();
        this.setAttribute('data-current-id', String(this.current.id));
        if (!this.startAutoplay()) {
          this.setAttribute('data-mode', 'manual');
          this.setAttribute('data-playing', 'false');
        }
      }
      this.updateControls();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('click', this.onDocClick, true);
      document.removeEventListener('change', this.onDocChange, true);
      document.removeEventListener('visibilitychange', this.onVisibility);
      reducedMotion.removeEventListener('change', this.onMotionChange);
      this._ready = false;
    }

    /* ---------------------------------------------------------------- wiring */

    bind() {
      this.onDocClick = (event) => this.handleColourEvent(event);
      this.onDocChange = (event) => this.handleColourEvent(event);
      this.onVisibility = () => {
        if (!document.hidden && this.playing) this.schedule();
      };
      this.onMotionChange = () => {
        if (reducedMotion.matches) this.stopAutoplay();
        this.updateControls();
      };

      document.addEventListener('click', this.onDocClick, true);
      document.addEventListener('change', this.onDocChange, true);
      document.addEventListener('visibilitychange', this.onVisibility);
      reducedMotion.addEventListener('change', this.onMotionChange);

      this.thumbsBox?.addEventListener('click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-thumb]') : null;
        if (!button) return;
        const item = this.byId.get(Number(button.getAttribute('data-id')));
        if (item) this.manualShow(item);
      });

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.manualStep(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.manualStep(1));
      this.playButton?.addEventListener('click', () => this.togglePlay());

      this.stage?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.manualStep(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.manualStep(1);
        }
      });

      this.bindSwipe();
      this.bindZoom();
    }



    /* ------------------------------------------------------------ colour picker */

    /**
     * @param {Event} event
     */
    handleColourEvent(event) {
      const input = this.colourInputFrom(event.target);
      if (input) this.chooseColour(input.value);
    }

    /**
     * Returns the colour radio that an event came from, or null (size options, other products, quick-add).
     * @param {EventTarget | null} target
     * @returns {HTMLInputElement | null}
     */
    colourInputFrom(target) {
      if (!this.colourOption || !(target instanceof Element)) return null;
      let input = target.closest('input[type="radio"]');
      if (!input) input = target.closest('label')?.querySelector('input[type="radio"]') || null;
      if (!(input instanceof HTMLInputElement)) return null;

      const picker = input.closest('variant-picker');
      if (!picker || picker.closest('product-card')) return null;
      // Only the picker of this gallery's own product counts (the product page's, or the quick-add pop-up's).
      if (this.productId && picker.getAttribute('data-product-id') !== this.productId) return null;

      const legend = input.closest('fieldset')?.querySelector('legend');
      const name = (legend?.textContent || '').trim().toLowerCase();
      return name === this.colourOption ? input : null;
    }

    /**
     * The visitor explicitly chose a colour: stop autoplay and show that colour's photo.
     * @param {string} colour
     */
    chooseColour(colour) {
      this.stopAutoplay();
      this.applyColour(colour);
      const own = this.items.find((item) => item.colours.includes(colour));
      const fallback = this.items.find((item) => !item.colours.length);
      const target = own || fallback || this.current;
      this.show(target, { announce: true });
      this.updateControls();
    }

    /**
     * @param {string} colour
     */
    applyColour(colour) {
      this.colour = colour;
      this.setAttribute('data-colour', colour);
      this.applyVisibility();
    }

    /* ------------------------------------------------------------------ photos */

    /** Photos currently offered by the thumbnails, arrows and swipe. */
    visible() {
      if (this.colour) {
        const own = this.items.filter((item) => item.colours.includes(this.colour));
        if (own.length) return [...own, ...this.items.filter((item) => !item.colours.length)];
      }
      return this.items;
    }

    /** Photos that autoplay may show: suitable garment photos among the visible ones. */
    autoplayItems() {
      return this.visible().filter((item) => item.suitable);
    }

    applyVisibility() {
      const visible = this.visible();
      const position = new Map(visible.map((item, index) => [item.id, index]));
      this.thumbItems.forEach((element, id) => {
        const index = position.get(id);
        element.hidden = index === undefined;
        element.style.order = index === undefined ? '' : String(index);
      });
      this.toggleAttribute('data-single', visible.length < 2);
    }

    /**
     * @param {object} item
     * @param {{instant?: boolean, announce?: boolean}} [options]
     */
    show(item, options = {}) {
      if (!item) return;
      this.current = item;
      this.setAttribute('data-current-id', String(item.id));
      const token = ++this.token;

      // Thumbnail state.
      this.thumbItems.forEach((element, id) => {
        const button = element.firstElementChild;
        if (!button) return;
        if (id === item.id) {
          button.setAttribute('aria-current', 'true');
          this.revealThumb(element, options.instant);
        } else {
          button.removeAttribute('aria-current');
        }
      });

      // Cross-fade to the new photo in the hidden layer, then swap.
      const next = this.layers[1 - this.activeLayer];
      const previous = this.layers[this.activeLayer];
      if (!next || !previous) return;

      const index = this.items.indexOf(item) + 1;
      next.alt = `${this.dataset.title || document.title.split(' – ')[0] || 'Product'} photo ${index}`;
      next.removeAttribute('aria-hidden');
      next.sizes = '(min-width: 750px) 640px, 100vw';
      next.srcset = srcset(item.src);
      next.src = sized(item.src, 1000);

      const swap = () => {
        if (token !== this.token) return;
        next.classList.add('is-active');
        previous.classList.remove('is-active');
        previous.setAttribute('aria-hidden', 'true');
        previous.alt = '';
        this.activeLayer = 1 - this.activeLayer;
      };

      if (typeof next.decode === 'function') {
        const guard = new Promise((resolve) => setTimeout(resolve, 1500));
        Promise.race([next.decode().catch(() => {}), guard]).then(swap);
      } else {
        swap();
      }

      if (options.announce && this.status) {
        const visible = this.visible();
        this.status.setAttribute('aria-live', 'polite');
        this.status.textContent = `Photo ${visible.indexOf(item) + 1} of ${visible.length}`;
      }

      this.preloadNeighbour();
    }

    preloadNeighbour() {
      const list = this.autoplayItems();
      const index = list.indexOf(this.current);
      const next = list[(index + 1) % list.length];
      if (next && next !== this.current) new Image().src = sized(next.src, 1000);
    }

    /**
     * Scroll the thumbnail list so the selected thumbnail is in view without moving the page.
     * @param {HTMLElement} element
     * @param {boolean} [instant]
     */
    revealThumb(element, instant) {
      const box = this.thumbsBox;
      if (!box || element.hidden) return;
      const boxRect = box.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      const vertical = box.scrollHeight > box.clientHeight + 1 && getComputedStyle(box).flexDirection === 'column';
      const behavior = instant || reducedMotion.matches ? 'auto' : 'smooth';
      if (vertical) {
        const offset = rect.top - boxRect.top - (box.clientHeight - rect.height) / 2;
        box.scrollTo({ top: box.scrollTop + offset, behavior });
      } else if (box.scrollWidth > box.clientWidth + 1) {
        const offset = rect.left - boxRect.left - (box.clientWidth - rect.width) / 2;
        box.scrollTo({ left: box.scrollLeft + offset, behavior });
      }
    }

    /* ------------------------------------------------------------- manual input */

    /**
     * @param {object} item
     */
    manualShow(item) {
      this.stopAutoplay();
      this.show(item, { announce: true });
    }

    /**
     * @param {number} direction
     */
    manualStep(direction) {
      const visible = this.visible();
      if (visible.length < 2) return;
      const index = Math.max(0, visible.indexOf(this.current));
      this.manualShow(visible[(index + direction + visible.length) % visible.length]);
    }

    /* ---------------------------------------------------------------- autoplay */

    startAutoplay() {
      if (reducedMotion.matches || this.autoplayItems().length < 2) return false;
      this.playing = true;
      this.setAttribute('data-playing', 'true');
      this.setAttribute('data-mode', 'auto');
      this.status?.setAttribute('aria-live', 'off');
      this.schedule();
      this.updateControls();
      return true;
    }

    stopAutoplay() {
      clearTimeout(this.timer);
      this.timer = 0;
      this.playing = false;
      this.setAttribute('data-playing', 'false');
      this.setAttribute('data-mode', 'manual');
      this.updateControls();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.tick(), this.intervalMs);
    }

    tick() {
      if (!this.playing) return;
      if (document.hidden || this.dialog?.open || !this.getClientRects().length) {
        this.schedule();
        return;
      }
      const list = this.autoplayItems();
      if (list.length < 2) {
        this.stopAutoplay();
        return;
      }
      const index = list.indexOf(this.current);
      this.show(list[(index + 1) % list.length]);
      this.schedule();
    }

    togglePlay() {
      if (this.playing) {
        this.stopAutoplay();
        return;
      }
      if (this.startAutoplay()) this.tick();
    }

    updateControls() {
      const button = this.playButton;
      if (!button) return;
      const hidden = reducedMotion.matches;
      button.hidden = hidden;
      const canPlay = this.autoplayItems().length >= 2;
      button.disabled = !this.playing && !canPlay;
      const label = this.playing ? 'Pause slideshow' : 'Play slideshow';
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
    }

    /* Swipe and zoom are added by mv-gallery-ui.js, which extends this class. */
    bindSwipe() {}

    bindZoom() {}
  }

  MvGallery.sized = sized;
  window.MvGalleryBase = MvGallery;

  // Safety net: if the UI script is missing, still register the element (without swipe and zoom).
  document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('mv-gallery')) customElements.define('mv-gallery', MvGallery);
  });
})();